import { useState } from 'react';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Case } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Input } from '@/new/components/shadcn/input';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/new/components/shadcn/tooltip';
import { KimMode } from '@/types';

interface ExampleKimQuestionProps {
  text: string;
  type: 'question' | 'draft';
  onClick: () => void;
}

export function ExampleKimQuestion({ text, type, onClick }: ExampleKimQuestionProps) {
  const isQuestion = type === 'question';

  return (
    <Tooltip>
      <TooltipTrigger
        onClick={onClick}
        className={`flex w-full cursor-pointer items-center gap-2 truncate rounded-md border bg-gray-50/50 p-1.5 text-xs text-gray-700 transition-all duration-300 hover:bg-gray-100`}
      >
        {isQuestion ? (
          <Icon className="lucide--lightbulb h-3.5 w-3.5 shrink-0 text-sky-500" />
        ) : (
          <Icon className="lucide--file-text h-3.5 w-3.5 shrink-0 text-green-500" />
        )}
        <span className="truncate">{text}</span>
        <Icon className="lucide--arrow-right text-teal-500" />
      </TooltipTrigger>
      <TooltipContent sideOffset={8} className="bg-gray-700 text-white">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

interface KimOverviewProps {
  caseObject: Case | undefined;
  onAskQuestion: (question: string, mode: KimMode) => void;
}

export const KimOverview = ({ caseObject, onAskQuestion }: KimOverviewProps) => {
  const [question, setQuestion] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    if (question.trim()) {
      onAskQuestion(question, KimMode.QuestionFlow);
      trackCustomEvent(TrackingEventsV2.AskKimFromSubsetSummary);
      setQuestion('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="space-y-4 lg:col-span-3">
      <div className="relative flex items-center">
        <Input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={'Ask Kim a question about this subset...'}
        />

        <button
          onClick={handleSubmit}
          className="absolute right-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full px-[9px] py-2 text-sm font-semibold text-white disabled:bg-gray-500"
        >
          <Icon className="lucide--arrow-right text-teal-500" />
        </button>
      </div>

      {caseObject &&
        Array.isArray(caseObject?.exampleKimQuestions?.question) &&
        caseObject.exampleKimQuestions.question.length > 0 && (
          <div className="mt-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className="space-y-2">
                  {caseObject.exampleKimQuestions.question.map((q: any, idx: number) => (
                    <ExampleKimQuestion
                      key={idx}
                      text={typeof q === 'string' ? q : q.prompt}
                      type="question"
                      onClick={() => {
                        onAskQuestion(q.prompt, KimMode.QuestionFlow);
                        trackCustomEvent(TrackingEventsV2.AskKimPresetQuestionFromSubsetSummary);
                      }}
                    />
                  ))}
                </div>
              </div>

              {caseObject &&
                Array.isArray(caseObject.exampleKimQuestions.draft) &&
                caseObject.exampleKimQuestions.draft.length > 0 && (
                  <div>
                    <div className="space-y-2">
                      {caseObject.exampleKimQuestions.draft.map((d: any, idx: number) => (
                        <ExampleKimQuestion
                          key={idx}
                          text={typeof d === 'string' ? d : d.prompt}
                          type="draft"
                          onClick={() => {
                            onAskQuestion(d.prompt, KimMode.DraftFlow);
                            trackCustomEvent(TrackingEventsV2.AskKimPresetQuestionFromSubsetSummary);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
    </div>
  );
};
